import { useCallback, useId, useMemo } from 'react';
import {
  Calendar,
  SlotInfo,
  ToolbarProps,
  dateFnsLocalizer,
} from 'react-big-calendar';
import { MonthDateHeader } from './components/MonthDateHeader';
import { Toolbar } from './components/Toolbar';
import { WeekHeader } from './components/WeekHeader';
import { Event } from './utils';
import { getTextColor } from 'libs/utils/src/generateColors';

import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import en from 'date-fns/locale/en-GB';
import fr from 'date-fns/locale/fr';
import { useBigCalendarContext } from './BigCalendarLocaleContext';

const locales = {
  en,
  fr,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const lang = {
  en: undefined, // it already has default values for English
  fr: {
    week: 'La semaine',
    work_week: 'Semaine de travail',
    day: 'Jour',
    month: 'Mois',
    previous: 'Antérieur',
    next: 'Prochain',
    today: `Aujourd'hui`,
    agenda: 'Ordre du jour',

    showMore: (total: number) => `+${total} plus`,
  },
  ar: {
    week: 'أسبوع',
    work_week: 'أسبوع العمل',
    day: 'يوم',
    month: 'شهر',
    previous: 'سابق',
    next: 'التالي',
    today: 'اليوم',
    agenda: 'جدول أعمال',

    showMore: (total: number) => `+${total} إضافي`,
  },
};

export type BigCalendarProps = {
  /**  List of events, each event should have a title, start and end dates (native js date type)
   * an optional allDay prop that specifies whether the event will take all day or just particular hours
   * an optional onClick handler that will fire when the event is clicked.
   */
  events: Event[];
  /** A handler that get executed whenever the month changes */
  onMonthChange?: (start: string, end: string) => void;
  /** Overrides default container classes, must have a height */
  className?: string;
  /** pass addEvent object to show addEvent button */
  addEvent?: { label: string; onClick: () => void };
  /** the starting view of the calendar */
  defaultView?: 'month' | 'week' | 'day';
  /** Constrains the minimum time of the Day and Week views. */
  min?: Date;
  /** Constrains the maximum time of the Day and Week views. */
  max?: Date;
  /** Allows mouse selection of ranges of dates/times. The 'ignoreEvents' option prevents selection code from running when a drag begins over an event. */
  selectable?: boolean | 'ignoreEvents';
  /** Callback fired when dragging a selection in the Time views. Only fires when selectable is true. */
  onSelectSlot?: ((slotInfo: SlotInfo) => void) | undefined;
  /** The number of slots per "section" in the time grid views.   */
  timeslots?: number;
  step?: number;
  defaultDate?: Date;
};

export default function BigCalendar({
  events,
  className,
  onMonthChange,
  addEvent,
  defaultView = 'month',
  min,
  max,
  selectable,
  onSelectSlot,
  step = 30,
  timeslots = 2,
  defaultDate,
}: BigCalendarProps) {
  const id = useId();
  const { locale } = useBigCalendarContext();
  const onSelectEvent = useCallback((calEvent: Event) => {
    if (calEvent.onClick) calEvent.onClick();
  }, []);
  const { messages, components } = useMemo(
    () => ({
      messages: lang[locale],
      components: {
        toolbar: (props: ToolbarProps<Event, object>) => (
          <Toolbar
            {...props}
            addEvent={addEvent}
            onMonthChange={onMonthChange}
            calendarId={id}
          />
        ),
        month: {
          dateHeader: MonthDateHeader,
        },
        week: {
          header: WeekHeader,
        },
      },
      onSelectEvent: null,
    }),
    [id, locale]
  );
  return (
    <div
      id={id}
      className={
        className
          ? className
          : 'h-[600px] bg-white rounded-lg overflow-hidden border-gray-200 border'
      }
    >
      <Calendar
        min={min}
        max={max}
        defaultView={defaultView}
        components={components}
        culture={locale}
        messages={messages}
        events={events}
        localizer={localizer}
        rtl={locale === 'ar'}
        views={['day', 'week', 'month']}
        onSelectEvent={onSelectEvent}
        selectable={selectable}
        onSelectSlot={onSelectSlot}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.color,
            color: event.color && getTextColor(event.color),
          },
        })}
        step={step}
        timeslots={timeslots}
        defaultDate={defaultDate}
      />
    </div>
  );
}
